import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { logger } from '@/utils/logger';
import { ROUTES } from '@/constants';

const ADMIN_RETURN_SESSION_KEY = 'admin_return_session';

interface AdminReturnSession {
  access_token: string;
  refresh_token: string;
  admin_email: string;
}

interface LoginAsResponse {
  success: boolean;
  data?: {
    hashed_token: string;
    verification_type: string;
    email: string;
  };
  error?: string;
}

export function useLoginAsUser() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const isLoginAs = useMemo(() => {
    return sessionStorage.getItem(ADMIN_RETURN_SESSION_KEY) !== null;
  }, []);

  const getLoginAsEmail = useCallback((): string | null => {
    const stored = sessionStorage.getItem(ADMIN_RETURN_SESSION_KEY);
    if (!stored) return null;
    try {
      const parsed = JSON.parse(stored) as AdminReturnSession;
      return parsed.admin_email;
    } catch {
      return null;
    }
  }, []);

  const loginAsUser = useCallback(async (targetUserId: string) => {
    setIsLoading(true);
    try {
      // 1. Save current admin session
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData?.session) {
        toast.error('No active session', { id: 'login-as-user' });
        return;
      }

      const adminReturn: AdminReturnSession = {
        access_token: sessionData.session.access_token,
        refresh_token: sessionData.session.refresh_token,
        admin_email: sessionData.session.user.email || 'admin',
      };

      // 2. Call edge function
      const { data, error } = await supabase.functions.invoke<LoginAsResponse>(
        'admin-login-as-user',
        { body: { target_user_id: targetUserId } }
      );

      // FunctionsHttpError exposes the response body via .context.json()
      let serverError: string | undefined;
      if (error && 'context' in error && error.context instanceof Response) {
        try {
          const body = await error.context.clone().json() as { error?: string };
          serverError = body?.error;
        } catch {
          // ignore parse failure
        }
      }

      if (error || !data?.data) {
        const rawMsg = serverError || data?.error || error?.message || 'Failed to generate session';
        // Friendlier copy for the common orphaned-profile case
        const msg = rawMsg === 'Target user not found'
          ? 'This profile has no auth account — login-as is not available for this user.'
          : rawMsg;
        toast.error(msg, { id: 'login-as-user' });
        logger.error('Login-as failed', { error: rawMsg, targetUserId });
        return;
      }

      // 3. Store admin session BEFORE switching
      sessionStorage.setItem(ADMIN_RETURN_SESSION_KEY, JSON.stringify(adminReturn));

      // 4. Establish session as target user
      const { error: otpError } = await supabase.auth.verifyOtp({
        token_hash: data.data.hashed_token,
        type: 'magiclink',
      });

      if (otpError) {
        // Rollback — remove stored session
        sessionStorage.removeItem(ADMIN_RETURN_SESSION_KEY);
        toast.error('Failed to establish session', { id: 'login-as-user' });
        logger.error('verifyOtp failed', { error: otpError.message });
        return;
      }

      toast.success(`Logged in as ${data.data.email}`, { id: 'login-as-user' });
      navigate(ROUTES.ACCOUNT_OVERVIEW);
    } catch (err) {
      sessionStorage.removeItem(ADMIN_RETURN_SESSION_KEY);
      toast.error('Unexpected error', { id: 'login-as-user' });
      logger.error('loginAsUser error', err);
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  const returnToAdmin = useCallback(async () => {
    setIsLoading(true);
    try {
      const stored = sessionStorage.getItem(ADMIN_RETURN_SESSION_KEY);
      if (!stored) {
        toast.error('No admin session to restore', { id: 'return-to-admin' });
        return;
      }

      const adminSession = JSON.parse(stored) as AdminReturnSession;

      // Sign out current (impersonated) user
      await supabase.auth.signOut();

      // Restore admin session
      const { error } = await supabase.auth.setSession({
        access_token: adminSession.access_token,
        refresh_token: adminSession.refresh_token,
      });

      sessionStorage.removeItem(ADMIN_RETURN_SESSION_KEY);

      if (error) {
        toast.error('Failed to restore admin session. Please log in again.', { id: 'return-to-admin' });
        logger.error('setSession failed', { error: error.message });
        navigate(ROUTES.AUTH);
        return;
      }

      toast.success('Returned to admin session', { id: 'return-to-admin' });
      navigate(ROUTES.ADMIN_OPERATIONS);
    } catch (err) {
      sessionStorage.removeItem(ADMIN_RETURN_SESSION_KEY);
      toast.error('Failed to restore session', { id: 'return-to-admin' });
      logger.error('returnToAdmin error', err);
      navigate(ROUTES.AUTH);
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  return {
    loginAsUser,
    returnToAdmin,
    isLoginAs,
    isLoading,
    getLoginAsEmail,
  };
}
